import {
  reactQueryGet,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";
import type { Message } from "@circle-react/types/CommunityInbox/Message";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { flatten } from "lodash";
import { useEffect } from "react";
import { useInfiniteQuery, useMutation } from "react-query";

import { useMessagingPaths } from "@/react/hooks/chatsV2/messaging/useMessagingPaths";
import { useCommunityInboxStore } from "@/react/hooks/chatsV2/store";

import { ACTIONS } from "./scrollConstants";

interface MessageListProps {
  id: number;
  isDms?: boolean;
  params?: any;
}

export const useMessageList = ({
  id,
  params = { per_page: 20 },
  isDms = false,
}: MessageListProps) => {
  const { setMessages, appendMessage, setScrollAction, setCurrentChatId } =
    useCommunityInboxStore();
  const pathname = useLocation({ select: location => location.pathname });
  const navigate = useNavigate();
  const { chatBotPath, newChatRoomPath } = useMessagingPaths();
  const isNewConversation = pathname.startsWith(newChatRoomPath());

  const fetchMessages = (page: number) =>
    reactQueryGet(
      internalApi.communityInbox.fetchMessages({
        id,
        params: {
          page,
          ...params,
        },
      }),
    );

  useEffect(() => {
    setCurrentChatId(id);
  }, [id, setCurrentChatId]);

  const { data, isInitialLoading, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ["community-inbox", "chat-room", "messages", id],
      queryFn: ({ pageParam = 1 }) => fetchMessages(pageParam),
      select: ({ pages = [] }) => ({
        pages: flatten(pages.map(page => page.records)),
        pageParams: pages.map(page => page.page),
        pagesList: pages,
      }),
      enabled: !!id,
      getNextPageParam: (lastPage: any) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (!data) return;
    if (data.pageParams?.length === 1) {
      setScrollAction(ACTIONS.FIRST_LOAD);
    }
    setMessages(data.pages);
  }, [data, setMessages, setScrollAction]);

  const { mutateAsync: createMessage } = useMutation({
    mutationFn: ({
      formValues,
      botChatRoomId,
    }: {
      formValues: any;
      botChatRoomId: number | null;
    }) => {
      const chatId = id || botChatRoomId;
      return reactQueryPost(
        internalApi.communityInbox.createMessage({
          id: Number(chatId),
        }),
        { ...formValues },
      );
    },
    onSuccess: (message: Message) => {
      setScrollAction(ACTIONS.NEW_MESSAGE);
      const type = isDms ? "member" : "admin";
      appendMessage(message, type);
      if (isNewConversation && message.community_inbox_chat_id) {
        void navigate({
          href: chatBotPath({
            id: String(message.community_inbox_chat_id),
          }),
        });
      }
    },
    onError: err => {
      console.error("Error while creating message", err);
    },
  });

  return {
    isMessagesLoading: isInitialLoading,
    createMessage,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
